<template>
  <component
    :is="tag"
    class="product-title"
  >
    <label
      v-if="brandDisplayName"
      :class="isMcom ? 'updated-brand-label large' : 'subtitle-1'"
      itemprop="brand"
      itemtype="https://schema.org/Brand"
      itemscope
    >
      <a
        class="link"
        :href="brandUrl"
        itemprop="url"
        @click="tagProductTitleClick"
        v-html="brandDisplayName"
      />
      <meta
        itemprop="name"
        content="{{ brandDisplayName }}"
      >
    </label>
    <component
      :is="productUrl ? 'a' : 'span'"
      v-if="titleText"
      v-bind="productUrl ? { href: productUrl, target: allowOpeningInNewTab ? '_blank' : '_self', } : {}"
      :class="isMcom ? 'body' : 'subtitle-2'"
      itemprop="name"
      v-html="titleText"
    />
  </component>
</template>

<script setup>
import { computed } from 'vue';
import { useBrand } from '@composables/useBrand';
import { useProductFlags } from '@modules/featureControl';

import tagProductTitleClick from '../../features/analytics/productTitle';

const { isMcom } = useBrand();
const { isChanelFlag } = useProductFlags();

const props = defineProps({
  /**
   * * Product name
   */
  productName: {
    type: String,
    required: true,
  },
  pdfEmailDescription: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: false,
    default: undefined, // Explicitly set default to undefined to avoid linter warning
  },
  /**
   * * Brand display name
   */
  brandDisplayName: {
    type: [String, undefined],
    required: false,
    default: undefined, // Explicitly set default to undefined to avoid linter warning
  },
  /**
   * * Brand url
   *
   * Will not be rendered if `brandDisplayName` is not provided
   */
  brandUrl: {
    type: String,
    required: false,
    default: undefined, // Explicitly set default to undefined to avoid linter warning
  },
  /**
   * * Product url
   *
   */
  productUrl: {
    type: String,
    required: false,
    default: '',
  },
  tag: {
    /**
     * @type {import('vue').PropType<keyof HTMLElementTagNameMap>}
     */
    // @ts-ignore JSDoc doesn't support correct PropType inference
    type: String,
    default: 'h1',
  },
  allowOpeningInNewTab: {
    type: Boolean,
    default: false,
  },
});

const titleText = computed(
  () => {
    if (isMcom) {
      return [props.description, props.productName].filter(Boolean).join('<br>');
    }
    const bcomProductName = isChanelFlag ? props.productName : props.pdfEmailDescription;
    return [bcomProductName, props.description].filter(Boolean).join('<br>');
  },
);
</script>

<style lang="scss" scoped>
@import "@core/csg/src/scss/typography/typography-mixins.scss";

.link:not(:hover):not(:active):not(:focus) {
  text-decoration: none;
}

.subtitle-1 {
  text-decoration: none;
}

.product-title {
  --gap: calc(#{map-get($grid-margin-gutters, "small")} / 2);
  display: flex;
  flex-direction: column;

  gap: var(--gap);
}
</style>

<style lang="scss" scoped brand="mcom">
@import "~settingSCSS";
@import "@core/csg/src/scss/typography/typography-mixins.scss";

.body {
  @include body(regular);
}

@include breakpoint(medium up) {
  .updated-brand-label {
    font-size: rem-calc(18);
  }
}
</style>

<style lang="scss" scoped brand="bcom">
.subtitle-1 {
    color: $black;
    font-weight: $font-weight-heavy;
    font-size: rem-calc(22);
    line-height: rem-calc(28);
    letter-spacing: $body-letter-spacing;
    text-transform: $global-text-transform;

    @include breakpoint(medium down) {
      font-size: rem-calc(20);
      line-height: rem-calc(25);
    }
  }

.subtitle-2 {
    color: $black;
    font-weight: 500;
    font-size: rem-calc(18);
    line-height: rem-calc(22);
    letter-spacing: 0em;
    text-transform: $global-text-transform;

    @include breakpoint(medium down) {
      font-size: $font-size-lg;
      line-height: $line-height-lg;
    }
  }
</style>
